import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { useSelector } from 'react-redux';
import Loader from '../markup/Loader';
import AtlasUnavailableMessage from './AtlasUnavailableMessage';
import {
	selectAtlasReferralUrl,
	selectAtlasStatus,
	selectAtlasIsLoading,
	selectAtlasUrlLoadStatus,
	selectAtlasError
} from '../../store/atlasSlice';

const AtlasEmbed = ({
	variant,
	className,
	style,
	visible,
	onUrlLoadError
}) => {
	const referralUrl = useSelector(selectAtlasReferralUrl);
	const status = useSelector(selectAtlasStatus);
	const loading = useSelector(selectAtlasIsLoading);
	const urlLoadStatus = useSelector(selectAtlasUrlLoadStatus);
	const urlError = useSelector(selectAtlasError);
	const reportedUrlErrorRef = useRef(false);

	useEffect(() => {
		if (urlLoadStatus === 'error' && urlError && onUrlLoadError && !reportedUrlErrorRef.current) {
			reportedUrlErrorRef.current = true;
			onUrlLoadError(urlError);
		}
	}, [urlLoadStatus, urlError, onUrlLoadError]);

	const isWidget = variant === 'widget';
	const showIframe = status === true && referralUrl;
	const isDown = status === 'down';
	const hasUrlError = urlLoadStatus === 'error';

	const containerClass = cx('atlas-embed', {
		'atlas-embed--widget': isWidget,
		'atlas-embed--hidden': visible === false,
		'app-loading-page': !isWidget && isDown,
		'app-loading-page--embedded': !isWidget && isDown
	}, className);

	if (isDown) {
		return (
			<div className={containerClass} style={style}>
				<div className={isWidget ? undefined : 'app-loading-page__content'}>
					<AtlasUnavailableMessage variant={variant} />
				</div>
			</div>
		);
	}

	if (hasUrlError && isWidget) {
		return (
			<div className={containerClass} style={style}>
				<p className='atlas-unavailable-message__text'>Unable to load Atlas. Please try again later.</p>
			</div>
		);
	}

	return (
		<div className={containerClass} style={style} aria-hidden={visible === false}>
			<Loader loading={loading} />
			{showIframe && (
				<iframe
					title='Atlas 2.0'
					src={referralUrl}
					width='100%'
					height='100%'
					frameBorder='0'
					allowFullScreen
					allow='clipboard-write; clipboard-read'
					className='atlas-embed__iframe'
				/>
			)}
		</div>
	);
};

AtlasEmbed.propTypes = {
	variant: PropTypes.oneOf(['page', 'widget']),
	className: PropTypes.string,
	style: PropTypes.object,
	visible: PropTypes.bool,
	onUrlLoadError: PropTypes.func
};

AtlasEmbed.defaultProps = {
	variant: 'page',
	visible: true
};

export default AtlasEmbed;
